import React from 'react';

import { LinkButton } from '@atlaskit/button/new';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalytics } from '@townsquare/analytics';
import { useFlagActions } from '@townsquare/flags';
import { useRouter } from '@townsquare/router/useRouter';

import { useTeamsSettingsContext } from '../../context/TeamsSettingsContext';
import { useTeamSettingsPath } from '../../hooks/useTeamSettingsPath';

const MAX_TEAM_TYPES = 25;

export const CreateTeamTypeButton = () => {
	const [, routerActions] = useRouter();
	const analytics = useAnalytics();
	const { teamTypesCount } = useTeamsSettingsContext();
	const path = useTeamSettingsPath('TEAM_TYPES_CREATE');
	const { addFlag } = useFlagActions();
	const { formatMessage } = useIntl();

	const onClick = React.useCallback(
		(e: React.MouseEvent<HTMLAnchorElement>) => {
			e.preventDefault();
			// Track analytics for create button click
			void analytics.ui('teamTypeCreateButton', 'clicked', {});

			// Skip navigation and show error flag if team types count exceeds the limit
			if (teamTypesCount >= MAX_TEAM_TYPES) {
				addFlag({
					id: 'team-type-create-button-limit-exceeded',
					type: 'error',
					shouldAutoDismiss: true,
					title: formatMessage({
						id: 'townsquare.teams-settings.header.create-team-type-button-limit-exceeded-title',
						defaultMessage: 'Unable to create type',
					}),
					description: formatMessage({
						id: 'townsquare.teams-settings.header.create-team-type-button-limit-exceeded-description',
						defaultMessage: 'Maximum amount of 25 team types has been reached. ',
					}),
				});
				return;
			}

			routerActions.push(path);
		},
		[analytics, teamTypesCount, routerActions, path, addFlag, formatMessage],
	);

	return (
		<LinkButton onClick={onClick} href={path} appearance="primary">
			<FormattedMessage
				id="townsquare.teams-settings.header.create-team-type-button"
				defaultMessage="Create type"
				description="Button to create a team type"
			/>
		</LinkButton>
	);
};
